<template>
  <div class="flex flex-col w-full gap-4">
    <div class="flex flex-col gap-1">
      <span class="font-medium">סוג הרישום</span>
      <div class="grid grid-cols-2 gap-2">
        <button
          v-for="option in kindOptions"
          :key="option.value"
          type="button"
          class="flex flex-col items-start gap-0.5 rounded-xl border-[1.5px] px-3 py-2.5 text-start transition-colors"
          :class="kind === option.value ? option.activeClass : 'border-gray-200 bg-white'"
          @click="kind = option.value"
        >
          <span class="text-[13.5px] font-semibold">{{ option.label }}</span>
          <span class="text-[11px] text-gray-500">{{ option.hint }}</span>
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <span class="font-medium">סכום</span>
      <InputNumber
        v-model="amount"
        mode="currency"
        currency="ILS"
        locale="he-IL"
        :min="0"
        :maxFractionDigits="2"
        placeholder="₪0.00"
        class="w-full"
        inputClass="w-full"
      />
    </div>

    <div class="flex flex-col gap-1">
      <span class="font-medium">הערה <span class="text-xs text-gray-400 font-normal">(לא חובה)</span></span>
      <InputText v-model="note" :placeholder="kind === 'debt' ? 'לדוגמה: יתרת תשלום על טיפול' : 'לדוגמה: החזר על ביטול'" />
    </div>

    <drawer-footer
      :approveDisable="!canSave"
      :approveLoading="saving"
      approveLabel="שמירה"
      cancelLabel="ביטול"
      @approve-click="save"
      @cancel-click="closeDrawer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'vue-toastification';
import InputNumber from 'primevue/inputnumber';
import { ICustomerBalanceResponse } from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';
import { CustomersApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps<{
  customerId: string;
  initialKind?: 'debt' | 'credit';
  onSaved?: (balance: ICustomerBalanceResponse) => void;
}>();

const toast = useToast();
const { closeDrawer } = useDrawer();

const kindOptions = [
  { value: 'debt' as const, label: 'חוב', hint: 'הלקוח/ה חייב/ת לעסק', activeClass: 'border-red-300 bg-red-50' },
  { value: 'credit' as const, label: 'יתרה', hint: 'זיכוי לשימוש בתשלום הבא', activeClass: 'border-green-300 bg-green-50' },
];

const kind = ref<'debt' | 'credit'>(props.initialKind ?? 'debt');
const amount = ref<number | null>(null);
const note = ref('');
const saving = ref(false);

const canSave = computed(() => !!amount.value && amount.value > 0);

const save = async () => {
  if (!canSave.value || saving.value) return;
  saving.value = true;
  try {
    const balance = await CustomersApi.addBalanceEntry({
      customerId: props.customerId,
      kind: kind.value,
      amount: amount.value!,
      note: note.value.trim() || undefined,
    });
    trackEvent('customer_balance_entry_added', { kind: kind.value });
    toast.success(kind.value === 'debt' ? 'החוב נרשם' : 'היתרה נרשמה');
    props.onSaved?.(balance);
    closeDrawer();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור, אנא נסו שוב');
  } finally {
    saving.value = false;
  }
};
</script>
